import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { formatBytes, formatTimestamp } from '@/lib/drive/format';

interface DriveTrashListProps {
  rows: { id: string; name: string; size: number; deletedAt: number }[];
  selectedId: string | null;
  locale?: string;
  onSelect: (id: string) => void;
}

/** The trash view's rows — flat deleted files with their deletion time. */
export function DriveTrashList({ rows, selectedId, locale, onSelect }: DriveTrashListProps) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <div className="flex min-h-48 flex-1 items-center justify-center rounded-lg border border-border-color bg-card">
        <p className="text-sm text-muted-foreground">{t('drive.trashEmpty')}</p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-x-auto rounded-lg border border-border-color bg-card">
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border-color text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="px-3 py-2 text-start font-medium">{t('drive.name')}</th>
            <th scope="col" className="w-24 px-3 py-2 text-start font-medium">{t('drive.size')}</th>
            <th scope="col" className="w-48 px-3 py-2 text-start font-medium">{t('drive.detailsModified')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row.id)}
              className={clsx(
                'cursor-default border-b border-border-color/50 last:border-b-0',
                selectedId === row.id ? 'bg-active' : 'hover:bg-muted',
              )}
            >
              <td className="min-w-0 px-3 py-2">
                <span className="truncate whitespace-nowrap text-foreground" title={row.name}>
                  {row.name}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {formatBytes(row.size)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {formatTimestamp(row.deletedAt, locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
